/**
 * CSV import, in two deliberate steps.
 *
 * The preview is not computed here. The file is sent to the import endpoint
 * with `dryRun: true`, which runs the identical parse, dedupe and suppression
 * logic the real write runs and returns the identical summary — so the numbers
 * an operator approves are a rehearsal of the write, not a second opinion about
 * it. Committing re-sends the same file.
 */

import { useState } from 'react';
import { FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useImportLeadCsv, type ImportSummary } from '@/hooks/crm/useLeadInbox';

interface ImportDialogProps {
  onClose: () => void;
}

interface LoadedFile {
  name: string;
  text: string;
}

const REJECTION_LABELS: Record<string, string> = {
  invalid_linkedin_url: 'Not a LinkedIn profile URL',
  urn_as_slug: 'LinkedIn internal id, not a profile URL',
};

function SummaryRow({ label, value, muted = false }: {
  label: string;
  value: number;
  muted?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className={muted ? 'text-sm text-muted-foreground' : 'text-sm'}>{label}</span>
      <span className={muted ? 'text-sm text-muted-foreground' : 'text-sm font-semibold'}>{value}</span>
    </div>
  );
}

export function ImportDialog({ onClose }: ImportDialogProps): JSX.Element {
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const importCsv = useImportLeadCsv();

  const handleFile = async (input: HTMLInputElement): Promise<void> => {
    const selected = input.files?.[0];
    if (!selected) return;

    setPreview(null);
    const text = await selected.text();
    const loaded = { name: selected.name, text };
    setFile(loaded);

    try {
      const result = await importCsv.mutateAsync({
        csv: text,
        fileName: selected.name,
        dryRun: true,
      });
      setPreview(result.summary);
    } catch (error) {
      setFile(null);
      toast.error(error instanceof Error ? error.message : 'This file could not be read.');
    }
  };

  const commit = async (): Promise<void> => {
    if (!file) return;
    try {
      const result = await importCsv.mutateAsync({
        csv: file.text,
        fileName: file.name,
        dryRun: false,
      });
      toast.success(
        `${result.summary.imported} lead${result.summary.imported === 1 ? '' : 's'} added to the queue`,
      );
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The import could not be written.');
    }
  };

  const busy = importCsv.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import leads from a CSV</DialogTitle>
          <DialogDescription>
            Any export carrying a LinkedIn profile URL works. Nothing is written until you
            confirm, and no lead becomes a contact without your approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="import-file">CSV file</Label>
            <Input
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={(event) => void handleFile(event.currentTarget)}
            />
          </div>

          {busy && preview === null && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Reading the file…
            </p>
          )}

          {preview && (
            <div className="rounded-lg border border-border bg-surface p-4">
              <p className="mb-2 text-sm font-medium">
                {preview.parsedRows} row{preview.parsedRows === 1 ? '' : 's'} read
              </p>
              <SummaryRow label="Will be added to the queue" value={preview.imported} />
              <SummaryRow label="Already a CRM contact" value={preview.suppressedExistingContact} muted />
              <SummaryRow label="Already in the queue" value={preview.alreadyInInbox} muted />
              <SummaryRow label="Duplicated inside this file" value={preview.duplicateInFile} muted />
              <SummaryRow label="Unusable rows" value={preview.rejectedCount} muted />

              {preview.rejected.length > 0 && (
                <div className="mt-3 border-t border-border pt-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Unusable rows
                  </p>
                  <ul className="mt-1 space-y-1">
                    {preview.rejected.map((rejection) => (
                      <li key={`${rejection.line}-${rejection.value}`} className="text-xs text-muted-foreground">
                        <span className="font-medium">Line {rejection.line}:</span>{' '}
                        {REJECTION_LABELS[rejection.reason] ?? rejection.reason}
                        {rejection.value !== '' && (
                          <span className="break-all" data-content-origin="external"> — {rejection.value}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {preview.rejectedCount > preview.rejected.length && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      and {preview.rejectedCount - preview.rejected.length} more.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" className="min-h-11 md:min-h-11" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="min-h-11 md:min-h-11"
              disabled={busy || preview === null || preview.imported === 0}
              onClick={() => void commit()}
            >
              <FileUp className="mr-2 h-4 w-4" aria-hidden="true" />
              {preview === null
                ? 'Import'
                : `Import ${preview.imported} lead${preview.imported === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
